import { getFileTypeLabel } from "../../types";
import type { BrowserItem, HomeSort } from "./homeBrowserTypes";

/** Leaf segment of a file record path ("docs/plan.md" → "plan.md"). */
export function fileNameFromPath(path: string): string {
  const segments = path.split("/").filter(Boolean);
  return segments[segments.length - 1] || path;
}

export function browserItemName(item: BrowserItem): string {
  switch (item.kind) {
    case "canvas":
      return item.data.name || "Untitled";
    case "file":
      return fileNameFromPath(item.data.path);
    case "chat":
      return item.data.flow_session?.name || "Untitled chat";
  }
}

/**
 * Case-insensitive name-substring match, used to narrow every browser tab from
 * one predicate. An empty/whitespace query matches everything. Searches only
 * the loaded page — the server's own limit still applies upstream.
 */
export function browserItemMatchesSearch(
  item: BrowserItem,
  query: string,
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return browserItemName(item).toLowerCase().includes(needle);
}

/** Type column label — main's old workspace files list vocabulary: files get
 *  their content-type label ("PDF", "PNG", "Spreadsheet"…), everything else
 *  its kind. Folder rows aren't `BrowserItem`s; they hardcode "Folder". */
export function browserItemTypeLabel(item: BrowserItem): string {
  switch (item.kind) {
    case "canvas":
      return "Canvas";
    case "file":
      return getFileTypeLabel(item.data.content_type);
    case "chat":
      return "Chat";
  }
}

export function browserItemUpdatedAt(item: BrowserItem): string | undefined {
  switch (item.kind) {
    case "canvas":
    case "file":
      return item.data.last_modified_at ?? item.data.updated_at;
    case "chat":
      return item.data.updated_at;
  }
}

/** Size column value in bytes. Only files carry one — the rest render "—". */
export function browserItemSize(item: BrowserItem): number | undefined {
  return item.kind === "file" && item.data.size > 0
    ? item.data.size
    : undefined;
}

const compareByName = (a: BrowserItem, b: BrowserItem) =>
  browserItemName(a).localeCompare(browserItemName(b), undefined, {
    sensitivity: "base",
  });

/** Ascending modified time. */
const compareByUpdated = (a: BrowserItem, b: BrowserItem) =>
  Date.parse(browserItemUpdatedAt(a) ?? "") -
  Date.parse(browserItemUpdatedAt(b) ?? "");

/** Whether the item has a real value for the field ("—" rows sink). */
const hasFieldValue = (item: BrowserItem, field: HomeSort["field"]) => {
  if (field === "modified")
    return !Number.isNaN(Date.parse(browserItemUpdatedAt(item) ?? ""));
  return true;
};

const FIELD_COMPARATORS: Record<
  HomeSort["field"],
  (a: BrowserItem, b: BrowserItem) => number
> = {
  name: compareByName,
  modified: compareByUpdated,
};

/**
 * Returns a new sorted array — react-query cache arrays must not be mutated.
 * `Array.prototype.sort` is stable, so ties keep server order (which is the
 * ground truth for Recents). Items with no value for the field (no size, no
 * parseable timestamp) sink to the bottom in either direction — flipping the
 * sort shouldn't surface a wall of "—" rows.
 */
export function sortBrowserItems(
  items: BrowserItem[],
  sort: HomeSort,
): BrowserItem[] {
  const compare = FIELD_COMPARATORS[sort.field];
  const sign = sort.dir === "asc" ? 1 : -1;
  return [...items].sort((a, b) => {
    const aHas = hasFieldValue(a, sort.field);
    const bHas = hasFieldValue(b, sort.field);
    if (aHas !== bHas) return aHas ? -1 : 1;
    if (!aHas) return 0;
    return sign * compare(a, b);
  });
}
